import type { AccountReasonCode } from '../models/project-account.model';
import type { AccountReconciliation, Money } from '../components/account-workspace/account-workspace.types';
import { processingStepLabelKeyOf } from './document-ai-state';
import { INTERPRETATION_REASON_KEYS, feedbackKeyOf } from './document-feedback-keys';

/** The AI card's word for a value the closing run took none from, then the reading pipeline's reasons. */
export function accountCardReasonKeyOf(reason: string | null | undefined): string {
    // The closing run read the documents and took no value from them — not a reading problem.
    if (reason === 'notUsedByRun') {
        return 'abcp_workspace_ai_not_used';
    }
    return feedbackKeyOf(INTERPRETATION_REASON_KEYS, reason);
}

/** The step on the "KI liest" card, labelled as in the documents list; the balance reading when unknown. */
export function accountCardStepKeyOf(step: string | null | undefined): string {
    return processingStepLabelKeyOf(step) || processingStepLabelKeyOf('READING');
}

/** The line under the chip, per reason code. Differenz has none: the card already shows the numbers. */
export const ACCOUNT_REASON_LINE_KEYS: Readonly<Record<AccountReasonCode, string>> = {
    DOCUMENT_MISSING: 'abcp_workspace_reason_document_missing',
    DOCUMENT_BEING_READ: 'abcp_workspace_reason_document_being_read',
    DOCUMENT_UNREADABLE: 'abcp_workspace_reason_document_unreadable',
    DOCUMENT_DATE_MISMATCH: 'abcp_workspace_reason_document_date_mismatch',
    PROOFS_CONTRADICT: 'abcp_workspace_reason_proofs_contradict',
    TRANSIT_NOT_BALANCED: 'abcp_workspace_reason_transit_not_balanced',
    RECONCILED_WITH_DOCUMENT: 'abcp_workspace_reason_reconciled_with_document',
};

/** Securities are reconciled on one of two read values; the line says which. */
export const RECONCILED_BASIS_LINE_KEYS: Readonly<Record<'MARKET_VALUE' | 'COST', string>> = {
    MARKET_VALUE: 'abcp_workspace_reason_reconciled_with_document_market_value',
    COST: 'abcp_workspace_reason_reconciled_with_document_cost',
};

/** How the view words the facts a line names; the view owns the language and the number format. */
export interface AccountReasonFormat {
    /** A document kind as its label. */
    kind(kind: string | null | undefined): string;
    /** An amount with its currency (`CHF 1'250.00`). */
    amount(money: Money | null | undefined): string;
    /** An amount without its currency, for the list of contradicting proofs. */
    number(money: Money | null | undefined): string;
    /** An ISO date (`2025-12-31`) as the user reads it. */
    date(iso: string | null | undefined): string;
}

export interface AccountReasonLine {
    key: string;
    params: Record<string, string>;
}

/**
 * The line under the chip, or `null` when there is nothing to say. Never under Differenz, and never
 * next to a hand-entered value: the card says "von Hand erfasst" there, and the stored reason belongs
 * to the machine's last look, which the entry overruled.
 */
export function accountReasonLineOf(
    rec: Pick<AccountReconciliation, 'status' | 'reason' | 'documentBalanceOrigin' | 'aiReading'>,
    format: AccountReasonFormat,
): AccountReasonLine | null {
    const reason = rec.reason;
    if (reason == null || rec.status === 'difference') {
        return null;
    }
    if (rec.documentBalanceOrigin === 'HUMAN' || rec.aiReading?.state === 'MANUAL') {
        return null;
    }
    const key = feedbackKeyOf(ACCOUNT_REASON_LINE_KEYS, reason.code);
    if (key === '') {
        return null;
    }
    switch (reason.code) {
        case 'DOCUMENT_MISSING':
            return { key, params: { kind: format.kind(reason.kind) } };
        case 'DOCUMENT_DATE_MISMATCH':
            return {
                key,
                params: {
                    date: format.date(reason.date),
                    balanceSheetDate: format.date(reason.balanceSheetDate),
                    amount: format.amount(reason.amount),
                },
            };
        case 'PROOFS_CONTRADICT':
            return {
                key,
                params: {
                    list: (reason.conflicting ?? [])
                        .map((proof) => `${format.kind(proof.kind)} ${format.number(proof.amount)}`)
                        .join(' / '),
                },
            };
        case 'TRANSIT_NOT_BALANCED':
            return { key, params: { amount: format.amount(reason.amount) } };
        case 'RECONCILED_WITH_DOCUMENT': {
            const basisKey = feedbackKeyOf(RECONCILED_BASIS_LINE_KEYS, reason.basis);
            return {
                key: basisKey || key,
                params: { kind: format.kind(reason.kind), date: format.date(reason.date) },
            };
        }
        default:
            return { key, params: {} };
    }
}

/** Every key a reason line can use — what the i18n guard checks. */
export const ACCOUNT_REASON_KEYS: readonly string[] = [
    ...Object.values(ACCOUNT_REASON_LINE_KEYS),
    ...Object.values(RECONCILED_BASIS_LINE_KEYS),
];
